import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Observable, of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { MeasurementToolsInterface } from '../../interfaces/measurement-tools.interface';
import { MeasurementToolsDetailsModel } from '../../models/low-level/measurement-tools-details.model';
import { TextModel } from '../../models/primitive/text.model';
import { initMeasurementTools } from '../../state/measurement-tools/actions/measurement-tools.actions';
import {
    selectCalibratedMeasurementTools,
    selectMeasurementTools,
    selectMeasurementToolsLoaded,
    selectMeasurementToolsStatus,
} from '../../state/measurement-tools/selectors/measurement-tools.selector';
import { LoadStatus } from '../../state/load-status';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { sortByGid } from '../../utilities/measurement-tools.utils';
import { selectSelectedUser } from '../../state/user-master';

/**
 * NgRx-backed implementation of {@link MeasurementToolsInterface}. The tools live in the
 * measurement-tools store slice; `init()` dispatches `initMeasurementTools` and waits for
 * the slice to settle. Getters read directly from the store via `selectSignal` so they always
 * reflect the latest state without requiring re-initialization.
 */
@Injectable({
    providedIn: 'root',
})
export class MeasurementToolsNgrxService implements MeasurementToolsInterface, InitializableInterface {
    public constructor(
        private readonly store: Store,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    public get isInitialized(): boolean {
        return this.store.selectSignal(selectMeasurementToolsLoaded)();
    }

    public init(): Observable<MeasurementToolsDetailsModel[]> {
        this.store.dispatch(initMeasurementTools());
        return this.store.select(selectMeasurementToolsStatus).pipe(
            filter((status) => status === LoadStatus.Loaded || status === LoadStatus.Error),
            take(1),
            switchMap((status) => (status === LoadStatus.Error ? of([]) : of(this.getMeasurementToolsDetailsList()))),
        );
    }

    public getMeasurementToolsDetailsList(): MeasurementToolsDetailsModel[] {
        return this.store
            .selectSignal(selectMeasurementTools)()
            .map((tool) => MeasurementToolsDetailsModel.fromState(tool));
    }

    public getOriginalDropdownEntries(): Array<DisplayDecorator<TextModel>> {
        return this.store
            .selectSignal(selectCalibratedMeasurementTools)()
            .map((tool) => {
                const displayName = `${tool.descriptionDropdownText} - ${tool.technicalId} - ${tool.gid}`;
                return new DisplayDecorator<TextModel>(displayName, new TextModel('TechIdentNo', tool.technicalId));
            });
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public setDropdownEntries(entries: Array<DisplayDecorator<TextModel>>): void {
        // No need to add dropdown entries from outside - this is being used by the confirmation-measurement-tools.model implmenetation and needs to be removed completely
    }

    public sortByGid(entries: Array<DisplayDecorator<TextModel>>): Array<DisplayDecorator<TextModel>> {
        const userGid = this.store.selectSignal(selectSelectedUser)()?.frontId ?? '';
        return sortByGid(entries, userGid);
    }
}
